*,
::after,
::before {
  box-sizing: border-box;
}

:host {
  /**
   * @prop --ch-popover-block-size:
   * Specifies the block size of the popover. Useful for scenarios where the
   * popover is resizable.
   * @default max-content
   */
  --ch-popover-block-size: max-content;
  /**
   * @prop --ch-popover-inline-size:
   * Specifies the inline size of the popover. Useful for scenarios where the
   * popover is resizable.
   * @default max-content
   */
  --ch-popover-inline-size: max-content;
  /**
   * @prop --ch-popover-max-block-size:
   * Specifies the maximum block size of the popover. Useful for scenarios where the
   * popover is resizable. Only px values are supported.
   * @default auto
   */
  --ch-popover-max-block-size: auto;
  /**
     * @prop --ch-popover-max-inline-size:
     * Specifies the maximum inline size of the popover. Useful for scenarios
     * where the popover is resizable. Only px values are supported.
     * @default auto
     */
  --ch-popover-max-inline-size: auto;
  /**
   * @prop --ch-popover-min-block-size:
   * Specifies the minimum block size of the popover. Useful for scenarios where the
   * popover is resizable.
   * @default auto
   */
  --ch-popover-min-block-size: auto;
  /**
    * @prop --ch-popover-min-inline-size:
    * Specifies the minimum inline size of the popover. Useful for scenarios
    * where the popover is resizable.
    * @default auto
    */
  --ch-popover-min-inline-size: auto;
  /**
   * @prop --ch-popover-resize-threshold:
   * Specifies the size of the threshold to resize the popover.
   * @default 4px
   */
  --ch-popover-resize-threshold: 8px;
  /**
   * @prop --ch-popover-separation-x:
   * Specifies the separation between the action and popover in the x axis.
   * @default 0px
   */
  --ch-popover-separation-x: 0px;
  /**
   * @prop --ch-popover-separation-y:
   * Specifies the separation between the action and popover in the y axis.
   * @default 0px
   */
  --ch-popover-separation-y: 0px;
  --ch-popover-resize-threshold--half-negative: calc(
    var(--ch-popover-resize-threshold) * -0.5
  );
  --ch-popover-border-inline-start-width: 0px;
  --ch-popover-border-inline-end-width: 0px;
  --ch-popover-border-block-start-width: 0px;
  --ch-popover-border-block-end-width: 0px;
  --ch-popover-resize__inline-start: calc(
    var(--ch-popover-resize-threshold--half-negative) -
      var(--ch-popover-border-inline-start-width) * 0.5
  );
  --ch-popover-resize__inline-end: calc(
    var(--ch-popover-resize-threshold--half-negative) -
      var(--ch-popover-border-inline-end-width) * 0.5
  );
  --ch-popover-resize__block-start: calc(
    var(--ch-popover-resize-threshold--half-negative) -
      var(--ch-popover-border-block-start-width) * 0.5
  );
  --ch-popover-resize__block-end: calc(
    var(--ch-popover-resize-threshold--half-negative) -
      var(--ch-popover-border-block-end-width) * 0.5
  );
  --ch-popover-x: calc(
    (
        var(--ch-popover-action-left) + var(--ch-popover-inline) +
          var(--ch-popover-dragged-x)
      ) * var(--ch-popover-rtl)
  );
  --ch-popover-y: calc(
    var(--ch-popover-action-top) + var(--ch-popover-block) +
      var(--ch-popover-dragged-y)
  );
  --ch-popover-dragged-x: 0px;
  --ch-popover-dragged-y: 0px;
  --ch-popover-rtl: 1;
  display: none;
  grid-auto-rows: max-content;
  position: fixed;
  inline-size: var(--ch-popover-inline-size);
  block-size: var(--ch-popover-block-size);
  min-inline-size: var(--ch-popover-min-inline-size);
  min-block-size: var(--ch-popover-min-block-size);
  max-inline-size: var(--ch-popover-forced-max-inline-size, var(--ch-popover-max-inline-size));
  max-block-size: var(--ch-popover-forced-max-block-size, var(--ch-popover-max-block-size));
  margin: 0;
  padding: 0;
  background-color: unset;
  border: unset;
  color: unset;
  overflow: visible;
  top: 0;
  inset-inline-start: 0;
  translate: var(--ch-popover-x) var(--ch-popover-y);
}

:host([overflow-behavior=add-scroll]) {
  overflow: auto;
  translate: max(0px, var(--ch-popover-x)) max(0px, var(--ch-popover-y));
}

:host(.ch-popover-rtl[overflow-behavior=add-scroll]) {
  translate: max(-100dvw, var(--ch-popover-x)) max(0px, var(--ch-popover-y));
}

:host([show]) {
  display: grid;
}

:host(.gx-popover-prevent-flickering) {
  visibility: hidden !important;
}

:host(.gx-popover-header-drag) {
  display: grid;
  grid-template-rows: max-content 1fr;
}

:host(.gx-popover-dragging) {
  cursor: move;
}
:host(.gx-popover-dragging) ::slotted(*) {
  pointer-events: none;
  user-select: none;
}

:host(.ch-popover-resizing) {
  pointer-events: none;
  user-select: none;
}

.edge__block-start, .edge__block-end {
  position: fixed;
  inset-inline: min(0px, (var(--ch-popover-resize-threshold) - var(--ch-popover-border-inline-start-width)) / 2) min(0px, (var(--ch-popover-resize-threshold) - var(--ch-popover-border-inline-end-width)) / 2);
  block-size: var(--ch-popover-resize-threshold);
  cursor: ns-resize;
}
.edge__inline-start, .edge__inline-end {
  position: fixed;
  inset-block: min(0px, (var(--ch-popover-resize-threshold) - var(--ch-popover-border-block-start-width)) / 2) min(0px, (var(--ch-popover-resize-threshold) - var(--ch-popover-border-block-end-width)) / 2);
  inline-size: var(--ch-popover-resize-threshold);
  cursor: ew-resize;
}
.edge__block-start {
  inset-block-start: var(--ch-popover-resize__block-start);
}
.edge__block-end {
  inset-block-end: var(--ch-popover-resize__block-end);
}
.edge__inline-start {
  inset-inline-start: var(--ch-popover-resize__inline-start);
}
.edge__inline-end {
  inset-inline-end: var(--ch-popover-resize__inline-end);
}

.corner__block-start-inline-start, .corner__block-end-inline-end {
  position: fixed;
  block-size: var(--ch-popover-resize-threshold);
  inline-size: var(--ch-popover-resize-threshold);
  cursor: nwse-resize;
}
.corner__block-start-inline-end, .corner__block-end-inline-start {
  position: fixed;
  block-size: var(--ch-popover-resize-threshold);
  inline-size: var(--ch-popover-resize-threshold);
  cursor: nesw-resize;
}
.corner__block-start-inline-start {
  inset-block-start: var(--ch-popover-resize__block-start);
  inset-inline-start: var(--ch-popover-resize__inline-start);
}
.corner__block-end-inline-end {
  inset-block-end: var(--ch-popover-resize__block-end);
  inset-inline-end: var(--ch-popover-resize__inline-end);
}
.corner__block-start-inline-end {
  inset-block-start: var(--ch-popover-resize__block-start);
  inset-inline-end: var(--ch-popover-resize__inline-end);
}
.corner__block-end-inline-start {
  inset-block-end: var(--ch-popover-resize__block-end);
  inset-inline-start: var(--ch-popover-resize__inline-start);
}

:host(.ch-popover-rtl) .corner__block-start-inline-start,
:host(.ch-popover-rtl) .corner__block-end-inline-end {
  cursor: nesw-resize;
}

:host(.ch-popover-rtl) .corner__block-start-inline-end,
:host(.ch-popover-rtl) .corner__block-end-inline-start {
  cursor: nwse-resize;
}

.resize-layer {
  position: fixed;
  inset: 0;
  pointer-events: none;
}